import { differenceInMonths, format } from 'date-fns';

/** How much of a stored date the candidate actually gave: `year` means the
 * month stored beside it is a placeholder. Null on rows saved before the
 * distinction existed, which read as `month`. */
export type DatePrecision = 'year' | 'month' | null | undefined;

const HALF_DAY_MS = 12 * 60 * 60 * 1000;

/**
 * The calendar day a profile date stands for, as a local Date.
 *
 * Profile dates are naive timestamps, and some were saved as local midnight
 * sent as UTC: 1 June in IST is stored as 31 May 18:30. Read as UTC, the
 * nearest midnight is the day that was picked, in any timezone within 12 hours
 * of UTC. Built back as a *local* date, so date-fns formatting and arithmetic
 * read that day no matter where the page is opened.
 */
export const toCalendarDate = (value?: string | null): Date | null => {
  const text = (value ?? '').trim();
  if (!text) return null;
  let iso = text;
  if (/^\d{4}$/.test(text)) iso = `${text}-01-01`;
  else if (/^\d{4}-\d{2}$/.test(text)) iso = `${text}-01`;
  else if (/T\d/.test(text) && !/(Z|[+-]\d{2}:?\d{2})$/.test(text)) {
    iso = `${text}Z`;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  const nearest = new Date(parsed.getTime() + HALF_DAY_MS);
  return new Date(
    nearest.getUTCFullYear(),
    nearest.getUTCMonth(),
    nearest.getUTCDate(),
  );
};

/** "Jun 2019", or "2019" when only the year was given; '' for no date. */
export const formatProfileDate = (
  value?: string | null,
  precision?: DatePrecision,
  monthPattern = 'MMM yyyy',
) => {
  const date = toCalendarDate(value);
  if (!date) return '';
  return format(date, precision === 'year' ? 'yyyy' : monthPattern);
};

/**
 * "Jun 2019 - Present", "2019 - 2023", or only the end ("2020") when that is
 * all there is. "Present" only for an entry flagged as ongoing: a missing end
 * date on a finished entry is just missing.
 */
export const formatProfileDateRange = ({
  start,
  startPrecision,
  end,
  endPrecision,
  isCurrent,
  monthPattern,
}: {
  start?: string | null;
  startPrecision?: DatePrecision;
  end?: string | null;
  endPrecision?: DatePrecision;
  isCurrent?: boolean | null;
  monthPattern?: string;
}) => {
  const from = formatProfileDate(start, startPrecision, monthPattern);
  const to = isCurrent
    ? 'Present'
    : formatProfileDate(end, endPrecision, monthPattern);
  if (from && to) return `${from} - ${to}`;
  return from || to;
};

const plural = (count: number, unit: string) =>
  count > 0 ? `${count} ${unit}${count > 1 ? 's' : ''}` : '';

/** "2 Years 3 Months" between the two dates (to today when ongoing). Years
 * only when either end is year-only -- its month is not real. */
export const formatProfileDuration = ({
  start,
  startPrecision,
  end,
  endPrecision,
  isCurrent,
}: {
  start?: string | null;
  startPrecision?: DatePrecision;
  end?: string | null;
  endPrecision?: DatePrecision;
  isCurrent?: boolean | null;
}) => {
  const from = toCalendarDate(start);
  const to = isCurrent ? new Date() : toCalendarDate(end);
  if (!from || !to || to < from) return '';
  const months = differenceInMonths(to, from);
  const years = Math.floor(months / 12);
  if (startPrecision === 'year' || (!isCurrent && endPrecision === 'year')) {
    return plural(years, 'Year');
  }
  return [plural(years, 'Year'), plural(months % 12, 'Month')]
    .filter(Boolean)
    .join(' ');
};

/**
 * A profile date as the month/year fields' value: "2019" when only the year
 * is known, "2019-06" otherwise. Sending this back -- rather than a timestamp
 * -- keeps the precision and is not shifted by the browser's timezone.
 */
export const toMonthYearValue = (
  value?: string | null,
  precision?: DatePrecision,
) => {
  const date = toCalendarDate(value);
  if (!date) return '';
  return format(date, precision === 'year' ? 'yyyy' : 'yyyy-MM');
};

const isYearOnly = (value?: string | null) =>
  /^\d{4}$/.test((value ?? '').trim());

/** -1, 0 or 1 by month. A year-only value matches every month of its year,
 * so "2019" is neither before nor after "2019-06". */
export const compareMonthYear = (a?: string | null, b?: string | null) => {
  const x = toCalendarDate(a);
  const y = toCalendarDate(b);
  if (!x || !y) return 0;
  if (x.getFullYear() !== y.getFullYear()) {
    return x.getFullYear() < y.getFullYear() ? -1 : 1;
  }
  if (isYearOnly(a) || isYearOnly(b)) return 0;
  return Math.sign(x.getMonth() - y.getMonth());
};
